import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";

import { URLS, SAGA_ACTIONS, EXTERNAL_AE_URL, EXTERNAL_URL } from "src/_config";
import SweetAlert from "react-bootstrap-sweetalert";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";

interface Job {
  emp_job_id: string;
  job_hash: string;
  job_title: string;
  city_name?: string;
  country_name?: string;
  posted_date: string;
  expiry_date: string;
  status: number;
  job_type?: string;
  workplace_type?: string;
  workplace_type_id?: number;
  master_job_id?: string;
  applicants?: number;
  newApplicants?: number;
  shortlisted?: number;
  finalDecision?: number;
  screening_count?: {
    applicant_count: number;
    screening_count: number;
    interview_count: number;
    final_decision_count: number;
    hired_count: number;
  };
}

interface WorkPlace {
  id: number;
  name: string;
  description: string;
  status: number;
}

export default function JobListingsSection() {
  const { t } = useTranslation("dashboard");
  const dispatch = useDispatch();
  const [jobListings, setJobListings] = useState<Job[]>([]);
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const toast = useToaster();

  console.log(userPermissions);
  // Redux selector for workplaces
  const { workplaces } = useSelector((state: any) => ({
    workplaces: state.global.workplaces,
  }));

  const workplacesData = workplaces && workplaces.length > 0 ? workplaces : null;

  const history = useHistory();
  const { callUpdateJobStatus, callCloneJob, callGetEmployerJobs, callDeleteJob } = useEmployerJobApi();
  const [searchPost, setSearchPost] = useState("");
  const [workplaceType, setWorkplaceType] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showCloneConfirm, setShowCloneConfirm] = useState(false);
  const [showUpdateStatusConfirm, setShowUpdateStatusConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [openMenuJobId, setOpenMenuJobId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [fixedTooltip, setFixedTooltip] = useState<{ jobId: string; top: number; left: number } | null>(null);
  const [hoveredTooltip, setHoveredTooltip] = useState<{ jobId: string; top: number; left: number } | null>(null);

  const isTouchDevice = () => window.matchMedia("(pointer: coarse)").matches;
  const itemsPerPage = 10; // One item per page as requested

  // Fetch workplaces data on component mount
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.WORK_PLACES,
      payload: { limit: 30, offset: 0 },
    });
  }, [dispatch]);

  // Helper function to map job status
  const getJobStatus = (status: number) => {
    switch (status) {
      case 0:
        return { text: t("dashboard.jobListings.table.status.inactive"), class: "status-inactive" };
      case 1:
        return { text: t("dashboard.jobListings.table.status.live"), class: "status-active" };
      case 2:
        return { text: t("dashboard.jobListings.table.status.expired"), class: "status-expired" };
      case 3:
        return { text: t("dashboard.jobListings.table.status.archived"), class: "status-archived" };
      case 9:
        return { text: t("dashboard.jobListings.table.status.pendingApproval"), class: "status-pending" };
      case 10:
        return { text: t("dashboard.jobListings.table.status.aiFiltering"), class: "status-filtering" };
      default:
        return { text: t("dashboard.jobListings.table.status.unknown"), class: "status-unknown" };
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Helper function to calculate days until expiry
  const getDaysUntilExpiry = (expiryDate: string) => {
    if (!expiryDate) return "";
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return t("dashboard.jobListings.table.expiry.expired");
    if (diffDays === 0) return t("dashboard.jobListings.table.expiry.expiresToday");
    return t("dashboard.jobListings.table.expiry.expiresIn", { days: diffDays });
  };

  // Pagination calculations
  const jobListingsArray = jobListings || [];
  const totalPages = Math.ceil(jobListingsArray.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = jobListingsArray.slice(startIndex, endIndex);

  console.log("Job Listings:", jobListingsArray);

  // Helper to compute progress bar segment widths for applicant overview
  const getApplicantProgressSegments = (sc: Job["screening_count"]) => {
    const s = sc?.screening_count ?? 0;
    const i = sc?.interview_count ?? 0;
    const f = sc?.final_decision_count ?? 0;
    const h = sc?.hired_count ?? 0;
    const total = s + i + f + h;
    if (total === 0) return { screening: "0%", interview: "0%", finalDecision: "0%", hired: "0%" };
    return {
      screening: `${Math.round((s / total) * 100)}%`,
      interview: `${Math.round((i / total) * 100)}%`,
      finalDecision: `${Math.round((f / total) * 100)}%`,
      hired: `${Math.round((h / total) * 100)}%`,
    };
  };

  // Helper to build tooltip text for applicant info icon
  const getTooltipContent = (sc: Job["screening_count"]) =>
    `Screening: ${sc?.screening_count ?? 0} | Interview: ${sc?.interview_count ?? 0} | Final Decision: ${sc?.final_decision_count ?? 0} | Hired: ${sc?.hired_count ?? 0}`;

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadJobPosting = canAccessMenu("read_my_jobs");
  const canEditJobPosting = canAccessMenu("edit_my_jobs");
  const canDeleteJobPosting = canAccessMenu("delete_my_jobs");

  const canEditAIPosting = canAccessMenu("edit_filter");
  const canDeleteAIPosting = canAccessMenu("delete_filter");

  // Function to refresh job listings
  const refreshJobListings = () => {
    // Get employer ID from user details
    const employerId = userDetails?.user?.company_id;

    if (!employerId) {
      console.error("Employer ID not found in user details");
      return;
    }

    const onSuccess = (message: string, data: any) => {
      // Update local state with jobs from API response
      if (data && data.jobs) {
        setJobListings(data.jobs);
        console.log("Job listings refreshed successfully");
      }
    };

    const onError = (message: string, error: any) => {
      console.error("Failed to refresh job listings:", error);
    };

    // Always include job_status array as default parameter
    callGetEmployerJobs(employerId, { job_status: ["ai_filter", "open", "rejected"] }, onSuccess, onError);
  };

  // Refresh job listings on component mount and when user data becomes available
  useEffect(() => {
    refreshJobListings();
  }, [userDetails?.user?.company_id]);

  // Close action dropdown when clicking outside
  useEffect(() => {
    if (!openMenuJobId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !(e.target as HTMLElement).closest(".action-menu-wrapper") &&
        !(e.target as HTMLElement).closest(".action-dropdown-menu")
      ) {
        setOpenMenuJobId(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuJobId]);

  // Close fixed tooltip when clicking outside
  useEffect(() => {
    if (!fixedTooltip) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".applicant-tooltip-wrapper") && !target.closest(".applicant-fixed-tooltip")) {
        setFixedTooltip(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [fixedTooltip]);

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === "member" && !canReadJobPosting) {
    return null;
  }
  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Function to handle search with filters
  const handleSearch = () => {
    // Get employer ID from user details
    const employerId = userDetails?.user?.company_id;

    if (!employerId) {
      console.error("Employer ID not found in user details");
      return;
    }

    // Validate date range
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toast.error("From date cannot be later than To date");
      return;
    }

    setIsSearching(true);

    // Build filter parameters
    const filterParams: any = {
      job_status: jobStatus ? [jobStatus] : ["ai_filter", "open", "rejected"], // Use selected status or default statuses
    };

    // Add workplace filter if selected
    if (workplaceType) {
      // Find the workplace ID by name
      const selectedWorkplace = workplacesData?.find((wp: WorkPlace) => wp.name === workplaceType);
      if (selectedWorkplace) {
        filterParams.workplace_type_id = selectedWorkplace.id;
      }
    }

    // Add search text filter if provided
    if (searchPost.trim()) {
      filterParams.search_text = searchPost.trim();
    }

    // Add date filters if provided
    if (fromDate) {
      filterParams["posted_date[from]"] = fromDate;
    }
    if (toDate) {
      filterParams["posted_date[to]"] = toDate;
    }

    console.log("Searching with filters:", filterParams);

    const onSuccess = (message: string, data: any) => {
      console.log("Job listings filtered successfully");
      // Update local state with filtered jobs from API response
      if (data && data.jobs) {
        setJobListings(data.jobs);
      }
      setCurrentPage(1); // Reset to first page when searching
      setIsSearching(false);
      setIsFiltered(true);
    };

    const onError = (message: string, error: any) => {
      console.error("Failed to filter job listings:", error);
      toast.error("Failed to search jobs. Please try again.");
      setIsSearching(false);
    };

    // Call API with filter parameters
    callGetEmployerJobs(employerId, filterParams, onSuccess, onError);
  };

  // Function to clear filters and refresh
  const handleClearFilters = () => {
    setSearchPost("");
    setWorkplaceType("");
    setJobStatus("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setIsFiltered(false);

    // Refresh with no filters
    refreshJobListings();
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handler to navigate to job details
  const handleJobClick = (jobId: string) => {
    const jobDetailsUrl = URLS.JOB_POSTING.JOB_DETAILS.replace(":jobId", jobId);
    history.push(jobDetailsUrl);
  };

  const handleEditJob = (jobId: any, jobStatus?: number) => {
    // If job status is 10 (AI filter), route to AI candidate search edit
    if (jobStatus === 10) {
      history.push(URLS.JOB_POSTING.AI_CANDIDATE_SEARCH_EDIT(jobId));
    } else {
      // Otherwise, route to regular job edit
      history.push(URLS.JOB_POSTING.EDIT(jobId));
    }
  };

  // Handler functions for action buttons
  const handleUpdateJobStatus = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowUpdateStatusConfirm(true);
  };

  const confirmUpdateJobStatus = () => {
    const onSuccess = (message: string, data: any) => {
      toast.success(message || "Job status updated successfully");
      // Refresh the job list after successful update
      refreshJobListings();
    };

    const onError = (message: string, error: any) => {
      toast.error(message || "Failed to update job status");
    };

    // Call the API with the job ID and any required data
    callUpdateJobStatus(
      selectedJobId,
      {
        // status: 0,
        status: 2,
      },
      onSuccess,
      onError,
    ); // Example: block user (status 0 = inactive)

    setShowUpdateStatusConfirm(false);
    setSelectedJobId("");
  };

  const cancelUpdateJobStatus = () => {
    setShowUpdateStatusConfirm(false);
    setSelectedJobId("");
  };

  const handleDeleteJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteJob = () => {
    callDeleteJob(
      selectedJobId,
      (message: string) => {
        toast.success(message || "Job deleted successfully");
        refreshJobListings();
      },
      (error: string) => {
        toast.error(error || "Failed to delete job");
      },
    );
    setShowDeleteConfirm(false);
    setSelectedJobId("");
  };

  const cancelDeleteJob = () => {
    setShowDeleteConfirm(false);
    setSelectedJobId("");
  };

  const handleCloneJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowCloneConfirm(true);
  };

  const confirmCloneJob = () => {
    const onSuccess = (message: string, data: any) => {
      toast.success(message || "Job reposted successfully");
      // Refresh the job list after successful repost
      refreshJobListings();
    };

    const onError = (message: string, error: any) => {
      toast.error(message || "Failed to repost job");
    };

    // Call the API with the job ID
    callCloneJob(selectedJobId, {}, onSuccess, onError);
    setShowCloneConfirm(false);
    setSelectedJobId("");
  };
  const cancelCloneJob = () => {
    setShowCloneConfirm(false);
    setSelectedJobId("");
  };

  return (
    <div className="job-listings-section">
      <div className="section-header">
        <h2 className="section-title">{t("dashboard.jobListings.title")}</h2>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.searchPost")}</label>
          <div className="dashboard-input-wrapper">
            <input
              type="text"
              placeholder={t("dashboard.jobListings.filters.searchPlaceholder")}
              value={searchPost}
              onChange={(e) => setSearchPost(e.target.value)}
              onKeyPress={handleKeyPress}
              className="filter-input"
            />
            <i className="dashboard-search-icon"></i>
          </div>
        </div>
        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.workplaceType")}</label>
          <select value={workplaceType} onChange={(e) => setWorkplaceType(e.target.value)} className="basic-select">
            <option value="">-- Select --</option>
            {workplacesData &&
              workplacesData.map((workplace: WorkPlace) => (
                <option key={workplace.id} value={workplace.name}>
                  {workplace.name}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.status")}</label>
          <select value={jobStatus} onChange={(e) => setJobStatus(e.target.value)} className="basic-select">
            <option value="">All Statuses</option>
            <option value="ai_filter">AI Filtering</option>
            <option value="open">Live</option>
            <option value="rejected">Rejected</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
            <option value="archived">Archived</option>
            <option value="pending_approval">Pending Approval</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.from")}</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            onClick={(e) => {
              try {
                const input = e.currentTarget as any;
                if (input.showPicker) {
                  input.showPicker();
                }
              } catch (error) {
                console.log("showPicker not supported");
              }
            }}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.to")}</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            onClick={(e) => {
              try {
                const input = e.currentTarget as any;
                if (input.showPicker) {
                  input.showPicker();
                }
              } catch (error) {
                console.log("showPicker not supported");
              }
            }}
            className="filter-input"
          />
        </div>
        <div className="filter-buttons">
          <button className="find-btn" onClick={handleSearch} disabled={isSearching}>
            {isSearching
              ? t("dashboard.jobListings.filters.searching") || "Searching..."
              : t("dashboard.jobListings.filters.findBtn")}
          </button>
          <button className="find-btn btn-cancel" onClick={handleClearFilters} disabled={isSearching}>
            {t("dashboard.jobListings.filters.clear")}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {/* {isFiltered && (
        <div className="search-results-summary">
          <p>
            {jobListingsArray.length === 0 
              ? t('dashboard.jobListings.results.noResults') || 'No jobs found with current filters'
              : t('dashboard.jobListings.results.found', { count: jobListingsArray.length }) || `Found ${jobListingsArray.length} job(s)`
            }
          </p>
        </div>
      )} */}

      {/* Job Listings Table */}
      <div className="table-container">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Job Title</th>
              <th>Created &amp; Expiring Dates</th>
              <th>Status</th>
              <th>Applicant Overview</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!currentPageData || currentPageData?.length == 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>
                  {t("dashboard.jobListings.table.emptyState")}
                </td>
              </tr>
            ) : (
              currentPageData?.map((job: any) => {
                const statusInfo = getJobStatus(job.status);
                const location =
                  job.city_name && job.country_name
                    ? `${job.city_name}, ${job.country_name}`
                    : job.city_name || job.country_name || t("dashboard.jobListings.table.locationNotSpecified");

                const sc = job.screening_count;
                const segments = getApplicantProgressSegments(sc);

                return (
                  <tr key={job.emp_job_id || job.job_hash} className="job-row">
                    {/* Cell 1: Job Title */}
                    <td data-label="Job Title">
                      <div className="job-title">
                        <Link
                          to={`/job-details/${job.emp_job_id}`}
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          {job.job_title}
                        </Link>
                      </div>
                      <div className="job-location">{location}</div>
                    </td>

                    {/* Cell 2: Created & Expiring Dates */}
                    <td data-label="Dates">
                      <div>
                        <span className="date-label">Created Date</span> {formatDate(job.posted_date)}
                      </div>
                      <div>
                        <span className="date-label">Expiring Date</span> {formatDate(job.expiry_date)}
                      </div>
                      <div>
                        <strong>{getDaysUntilExpiry(job.expiry_date)}</strong>
                      </div>
                    </td>

                    {/* Cell 3: Status + workplace type */}
                    <td data-label="Status">
                      <span className={`status-badge ${statusInfo.class}`}>
                        <i className="status-dot"></i>
                        {statusInfo.text}
                      </span>
                      <div className="workplace-type-sub">{job.workplace_type || ""}</div>
                    </td>

                    {/* Cell 4: Applicant Overview */}
                    <td data-label="Applicant Overview">
                      <div className="applicant-overview">
                        <div className="applicant-overview-row">
                          <span className="applicants-big-count">{sc?.applicant_count ?? 0}</span>
                          {(sc?.screening_count ?? 0) > 0 && (
                            <span className="new-to-review-badge">{sc!.screening_count} New to Review!</span>
                          )}
                          <div
                            className="applicant-tooltip-wrapper"
                            onClick={(e) => {
                              if (!isTouchDevice()) return;
                              e.stopPropagation();
                              if (fixedTooltip?.jobId === job.emp_job_id) {
                                setFixedTooltip(null);
                              } else {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setFixedTooltip({
                                  jobId: job.emp_job_id,
                                  top: rect.bottom + window.scrollY + 8,
                                  left: rect.left + window.scrollX,
                                });
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (isTouchDevice()) return;
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setHoveredTooltip({
                                jobId: job.emp_job_id,
                                top: rect.bottom + window.scrollY + 8,
                                left: rect.left + window.scrollX,
                              });
                            }}
                            onMouseLeave={() => {
                              if (isTouchDevice()) return;
                              setHoveredTooltip(null);
                            }}
                          >
                            <i className="fa fa-info-circle applicant-info-trigger"></i>
                          </div>
                        </div>
                        <div
                          className="applicant-progress-bar-wrapper"
                          onMouseEnter={(e) => {
                            if (isTouchDevice()) return;
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setHoveredTooltip({
                              jobId: job.emp_job_id,
                              top: rect.bottom + window.scrollY + 8,
                              left: rect.left + window.scrollX,
                            });
                          }}
                          onMouseLeave={() => {
                            if (isTouchDevice()) return;
                            setHoveredTooltip(null);
                          }}
                        >
                          <div className="applicant-progress-bar">
                            <div
                              className="bar-segment bar-segment--screening"
                              style={{ width: segments.screening }}
                            ></div>
                            <div
                              className="bar-segment bar-segment--interview"
                              style={{ width: segments.interview }}
                            ></div>
                            <div
                              className="bar-segment bar-segment--final-decision"
                              style={{ width: segments.finalDecision }}
                            ></div>
                            <div className="bar-segment bar-segment--hired" style={{ width: segments.hired }}></div>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Cell 5: Action */}
                    <td data-label="Action">
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        {/* Standalone quick-action button beside 3-dots */}
                        {(() => {
                          const isJobPauseable = job.status === 1 || job.status === 10;
                          if (
                            isJobPauseable &&
                            ((job.status === 10 && canDeleteAIPosting) || (job.status !== 10 && canDeleteJobPosting))
                          ) {
                            return (
                              <button
                                className="action-btn"
                                data-tooltip={t("dashboard.jobListings.table.actions.stopJob")}
                                onClick={() => handleUpdateJobStatus(job.emp_job_id)}
                              >
                                <i className="fa fa-pause"></i>
                              </button>
                            );
                          } else if (
                            !job.master_job_id &&
                            ((job.status === 10 && canEditAIPosting) || (job.status !== 10 && canEditJobPosting))
                          ) {
                            return (
                              <button
                                className="action-btn"
                                data-tooltip={
                                  job.status === 2
                                    ? t("dashboard.jobListings.table.actions.republishJob")
                                    : t("dashboard.jobListings.table.actions.editJob")
                                }
                                onClick={() => handleEditJob(job.emp_job_id, job.status)}
                              >
                                <i className="fa fa-edit"></i>
                              </button>
                            );
                          }
                          return null;
                        })()}

                        {/* Three-dot menu */}
                        <div className="action-menu-wrapper">
                          <button
                            className="action-btn"
                            id={`action-btn-${job.emp_job_id}`}
                            onClick={(e) => {
                              if (openMenuJobId === job.emp_job_id) {
                                setOpenMenuJobId(null);
                                setDropdownPosition(null);
                              } else {
                                setOpenMenuJobId(job.emp_job_id);
                                // Calculate position relative to button for mobile
                                const button = e.currentTarget;
                                const rect = button.getBoundingClientRect();
                                setDropdownPosition({
                                  top: rect.bottom + window.scrollY + 4, // Below button with scroll offset
                                  left: rect.right + window.scrollX - 170, // Align to right edge (170px is menu width)
                                });
                              }
                            }}
                          >
                            {openMenuJobId === job.emp_job_id ? (
                              <i className="fa fa-times"></i>
                            ) : (
                              <i className="fa fa-ellipsis-v"></i>
                            )}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Fixed tooltip — rendered outside table to escape overflow:auto clipping */}
      {/* hoveredTooltip = desktop hover (info icon or progress bar); fixedTooltip = mobile click */}
      {(hoveredTooltip || fixedTooltip) &&
        (() => {
          const active = hoveredTooltip ?? fixedTooltip!;
          const tooltipJob = jobListingsArray.find((j: any) => j.emp_job_id === active.jobId);
          const tsc = tooltipJob?.screening_count;
          return (
            <div
              className="applicant-fixed-tooltip"
              style={{ position: "absolute", top: active.top, left: active.left, zIndex: 99999 }}
            >
              <div className="tooltip-row tooltip-screening">Screening: {tsc?.screening_count ?? 0}</div>
              <div className="tooltip-row tooltip-interview">Interview: {tsc?.interview_count ?? 0}</div>
              <div className="tooltip-row tooltip-final-decision">Final Decision: {tsc?.final_decision_count ?? 0}</div>
              <div className="tooltip-row tooltip-hired">Hired: {tsc?.hired_count ?? 0}</div>
            </div>
          );
        })()}

      {/* Action dropdown menu — rendered outside table to escape overflow clipping */}
      {openMenuJobId &&
        dropdownPosition &&
        (() => {
          const job = jobListingsArray.find((j: any) => j.emp_job_id === openMenuJobId);
          if (!job) return null;

          return (
            <div
              className="action-dropdown-menu action-dropdown-menu-portal"
              style={{
                position: "absolute",
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 99999,
              }}
            >
              {canReadJobPosting && (
                <button
                  className="action-dropdown-item"
                  onClick={() => {
                    setOpenMenuJobId(null);
                    setDropdownPosition(null);
                    handleJobClick(job.emp_job_id || job.job_hash);
                  }}
                >
                  <i className="fa fa-eye"></i> View Applicants
                </button>
              )}
              {job.master_job_id && (
                <button
                  className="action-dropdown-item"
                  onClick={() => {
                    setOpenMenuJobId(null);
                    setDropdownPosition(null);
                    const baseUrl = job.country_name === "UAE" ? EXTERNAL_AE_URL : EXTERNAL_URL;
                    window.open(`${baseUrl}job/view/${job.master_job_id}`, "_blank");
                  }}
                >
                  <i className="fa fa-external-link"></i> View Job
                </button>
              )}
              {(job.status === 1 || job.status === 10) &&
                ((job.status === 10 && canDeleteAIPosting) || (job.status !== 10 && canDeleteJobPosting)) && (
                  <button
                    className="action-dropdown-item"
                    onClick={() => {
                      setOpenMenuJobId(null);
                      setDropdownPosition(null);
                      handleUpdateJobStatus(job.emp_job_id);
                    }}
                  >
                    <i className="fa fa-pause"></i> Hide/Pause
                  </button>
                )}
              {!job.master_job_id &&
                ((job.status === 10 && canEditAIPosting) || (job.status !== 10 && canEditJobPosting)) && (
                  <button
                    className="action-dropdown-item"
                    onClick={() => {
                      setOpenMenuJobId(null);
                      setDropdownPosition(null);
                      handleEditJob(job.emp_job_id, job.status);
                    }}
                  >
                    <i className="fa fa-edit"></i> Edit Actions
                  </button>
                )}
              {!job.master_job_id && job.expiry_date && new Date(job.expiry_date) < new Date() && (
                <button
                  className="action-dropdown-item"
                  onClick={() => {
                    setOpenMenuJobId(null);
                    setDropdownPosition(null);
                    handleCloneJob(job.emp_job_id);
                  }}
                >
                  <i className="fa fa-retweet"></i> Repost
                </button>
              )}
              {((job.status === 10 && canDeleteAIPosting) || (job.status !== 10 && canDeleteJobPosting)) && (
                <button
                  className="action-dropdown-item"
                  onClick={() => {
                    setOpenMenuJobId(null);
                    setDropdownPosition(null);
                    handleDeleteJob(job.emp_job_id);
                  }}
                >
                  <i className="fa fa-trash"></i> Delete
                </button>
              )}
            </div>
          );
        })()}

      {/* Pagination */}
      {currentPageData?.length > 0 && totalPages > 1 ? (
        <div className="pagination">
          <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
            {t("dashboard.jobListings.pagination.prev")}
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`}>...</span>
              ) : (
                <button
                  key={page}
                  className={`page-num ${currentPage === page ? "active" : ""}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              ),
            )}
          </div>
          <button className="page-btn" onClick={handleNextPage} disabled={currentPage === totalPages}>
            {t("dashboard.jobListings.pagination.next")}
          </button>
        </div>
      ) : null}

      {/* Clone Job Confirmation */}
      {showCloneConfirm && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={t("dashboard.jobListings.cloneConfirm.confirmBtn")}
          confirmBtnBsStyle="preview"
          cancelBtnText={t("dashboard.jobListings.cloneConfirm.cancelBtn")}
          cancelBtnBsStyle="cancel"
          title={t("dashboard.jobListings.cloneConfirm.title")}
          onConfirm={confirmCloneJob}
          onCancel={cancelCloneJob}
        >
          {t("dashboard.jobListings.cloneConfirm.message")}
        </SweetAlert>
      )}

      {/* Update Job Status Confirmation */}
      {showUpdateStatusConfirm && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Update Status"
          confirmBtnBsStyle="preview"
          cancelBtnText={t("dashboard.jobListings.cloneConfirm.cancelBtn")}
          cancelBtnBsStyle="cancel"
          title="Update Job Status"
          onConfirm={confirmUpdateJobStatus}
          onCancel={cancelUpdateJobStatus}
        >
          Are you sure you want to update this job status?
        </SweetAlert>
      )}

      {/* Delete Job Confirmation */}
      {showDeleteConfirm && (
        <SweetAlert
          danger
          showCancel
          confirmBtnText="Delete"
          confirmBtnBsStyle="preview"
          cancelBtnText={t("dashboard.jobListings.cloneConfirm.cancelBtn")}
          cancelBtnBsStyle="cancel"
          title="Delete Job"
          onConfirm={confirmDeleteJob}
          onCancel={cancelDeleteJob}
        >
          Are you sure you want to delete this job? This action cannot be undone.
        </SweetAlert>
      )}
    </div>
  );
}
